import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { DownloadOutlined } from '@ant-design/icons'

import { getTemplateurl, sendProposalToProspect } from 'core/application/ichraDecisionTool/dashboard/prospectProposal'
import { uploadProposal } from 'core/application/ichraDecisionTool/dashboard/uploadProposal'

import { commandRequest, queryRequest } from 'common/RequestUtils'
import { BackNavigation, Button, Card, Col, FileUpload, Notification, Row } from 'common/components'
import { useQueryParamsV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import './Proposal.less'

const { LOGIN_PATH, ICHRA_DECISION_TOOL_PATH, PROSPECTS_PATH } = ROUTE_PATHS

const Proposal = () => {
  const { id, companyName } = useQueryParamsV2()
  const history = useHistory()
  const { t: translate } = useTranslation('ichra-decision-tool')
  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [sending, setSending] = useState<boolean>(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState<boolean>(false)

  useEffect(() => {
    if (!(companyName && id)) {
      history.push(LOGIN_PATH)
    }
  }, [id, companyName])

  const downloadTemplate = async () => {
    setDownloadingTemplate(true)
    const result = await queryRequest(() => getTemplateurl(id ?? ''))
    setDownloadingTemplate(false)
    if (!result.errors) {
      window.open(result, '_blank')
    } else {
      Notification.open({
        message: result.errors.join(','),
        type: 'error',
      })
    }
  }

  const sendProposal = async () => {
    setSending(true)
    uploadFileToS3()
      .then((res) => {
        sendProspectProposal(res.fileId)
      })
      .catch((err) => {
        Notification.open({
          message: translate('error-uploading-file'),
          type: 'error',
        })
      })
      .finally(() => setSending(false))
  }

  const uploadFileToS3 = async (): Promise<any> => {
    if (uploadedFile) {
      const uploadFileResult = await commandRequest(() => uploadProposal(uploadedFile, id ?? ''))
      if (!uploadFileResult.errors) {
        return Promise.resolve(uploadFileResult)
      } else {
        return Promise.reject()
      }
    } else {
      return Promise.reject()
    }
  }

  const sendProspectProposal = async (fileName: string) => {
    const res = await commandRequest(() => sendProposalToProspect(fileName, id ?? ''))
    if (!res.errors) {
      Notification.open({
        message: translate('proposal.proposal-sent'),
        type: 'success',
      })
      history.push(`${ICHRA_DECISION_TOOL_PATH}/${PROSPECTS_PATH}`)
    } else {
      console.log(res.errors)
      Notification.open({
        message: res.errors.join(','),
        type: 'error',
      })
    }
  }

  return (
    <Row>
      <Col>
        <Card
          title={
            <BackNavigation title={translate('proposal.title').replace('[]', companyName ?? '')} history={history} />
          }
        >
          <div className="flex flex__center flex__direction_column ">
            <div>
              <div>
                <h4>{translate('proposal.download-the-template')}</h4>
                <p className="color-gray-8"></p>
                <Button
                  customIcon={<DownloadOutlined />}
                  className="mt-8"
                  onClick={downloadTemplate}
                  loading={downloadingTemplate}
                >
                  {translate('download')}
                </Button>
              </div>
              <div className="mt-32">
                <div className="border_bottom  pb-24">
                  <h4>{translate('proposal.upload-the-proposal')}</h4>
                  <p className="color-gray-8"></p>
                  <div className="mt-24">
                    <FileUpload
                      uploadTitle={translate('proposal.upload-proposal')}
                      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,application/pdf"
                      uploading={uploading}
                      choosedFileName={uploadedFile ? uploadedFile.name : ''}
                      onFileChoosed={(file: File) => setUploadedFile(file)}
                      onDeleteFileChoosed={() => setUploadedFile(null)}
                    />
                  </div>
                </div>
                <div className="mt-16">
                  <Button
                    type="primary"
                    icon="far fa-paper-plane"
                    className="mt-8"
                    onClick={sendProposal}
                    disabled={!uploadedFile}
                    loading={sending}
                  >
                    {translate('proposal.send-to-prospect')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  )
}

export default Proposal
